import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Loader2, 
  CalendarDays, 
  Clock, 
  Check, 
  X, 
  Palmtree,
  Trash2,
  Edit
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { toast } from 'sonner';

const VACATION_STATUS_COLORS = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  denied: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export function LeaveManagementTab({ 
  leaveSummary, 
  leaveRequests, 
  leaveLoading, 
  staff,
  api,
  fetchLeaveRequests,
  fetchLeaveSummary,
  fetchStaff,
  openLeaveReviewDialog,
  handleDeleteLeaveRequest
}) {
  const handleApprove = async (requestId) => {
    try {
      await api.put(`/vacation-requests/${requestId}`, { status: 'approved', admin_notes: '' });
      toast.success('Leave approved - holiday balance updated');
      fetchLeaveRequests();
      fetchLeaveSummary();
      fetchStaff();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-amber-400">{leaveSummary.pending_requests}</div>
            <div className="text-sm text-gray-400">Pending Requests</div>
          </CardContent>
        </Card>
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-green-400">{leaveSummary.approved_this_month}</div>
            <div className="text-sm text-gray-400">Approved This Month</div>
          </CardContent>
        </Card>
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-400">{leaveSummary.total_days_booked_this_month}</div>
            <div className="text-sm text-gray-400">Days Booked</div>
          </CardContent>
        </Card>
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-purple-400">{leaveSummary.employees_on_leave_today}</div>
            <div className="text-sm text-gray-400">On Leave Today</div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Requests List */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-md">
              <CalendarDays className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Leave Requests</CardTitle>
              <CardDescription className="text-gray-400">Review and manage employee leave requests</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {leaveLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
            </div>
          ) : leaveRequests.length === 0 ? (
            <div className="text-center py-8">
              <Palmtree className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400">No leave requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaveRequests.map((request) => {
                const days = differenceInDays(parseISO(request.end_date), parseISO(request.start_date)) + 1;
                const staffMember = staff.find(s => s.id === request.employee_id);
                return (
                  <div 
                    key={request.id}
                    className="flex flex-col lg:flex-row lg:items-center justify-between p-4 bg-[#1E2530] rounded-lg gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-white font-medium">{request.employee_name}</span>
                        <Badge className={VACATION_STATUS_COLORS[request.status]}>
                          {request.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                          {request.status === 'approved' && <Check className="w-3 h-3 mr-1" />}
                          {request.status === 'denied' && <X className="w-3 h-3 mr-1" />}
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-4 h-4" />
                          {format(parseISO(request.start_date), 'dd MMM')} - {format(parseISO(request.end_date), 'dd MMM yyyy')}
                        </span>
                        <span className="text-[#F5A623] font-medium">{days} day{days !== 1 ? 's' : ''}</span>
                        {staffMember && (
                          <span className="text-gray-500">
                            Balance: {staffMember.holidays_remaining} days remaining
                          </span>
                        )}
                      </div>
                      {request.reason && (
                        <p className="text-sm text-gray-500 mt-2">Reason: {request.reason}</p>
                      )}
                      {request.admin_notes && (
                        <div className="mt-2 p-2 bg-[#2A3240] rounded text-xs text-gray-400">
                          <span className="font-semibold">Admin Note:</span> {request.admin_notes}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {request.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(request.id)}
                            className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openLeaveReviewDialog(request)}
                            className="border-white/20 text-gray-300 hover:bg-white/5"
                          >
                            Review
                          </Button>
                        </>
                      )}
                      {request.status !== 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openLeaveReviewDialog(request)}
                          className="border-white/20 text-gray-300 hover:bg-white/5"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteLeaveRequest(request.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
